import React, { useEffect, useState, useCallback } from "react";
import { Layout } from "../components/Layout";
import { api } from "../lib/api";
import { toast } from "sonner";
import { InstagramLogoIcon, PlugIcon, TrashIcon, ArrowsClockwiseIcon, BroadcastIcon } from "@phosphor-icons/react";
import { Switch } from "../components/ui/switch";

export default function Connections() {
  const [pages, setPages] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([api.listPages(), api.listConnections()]);
      setPages(p.pages || []);
      setConnections(c || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load accounts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const enabledFor = (igId) => connections.find((c) => c.ig_business_account_id === igId);

  const onEnable = async (page) => {
    if (!page.instagram_business_account) return;
    setBusy(page.id);
    try {
      await api.createConnection({
        fb_page_id: page.id,
        fb_page_name: page.name,
        ig_business_account_id: page.instagram_business_account.id,
        ig_username: page.instagram_business_account.username,
        page_access_token: page.access_token,
      });
      toast.success(`Enabled @${page.instagram_business_account.username}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(null);
    }
  };

  const onToggle = async (conn, enabled) => {
    await api.toggleConnection(conn.id, enabled);
    load();
  };

  const onDelete = async (conn) => {
    if (!window.confirm(`Remove connection to @${conn.ig_username}? Keywords will also be deleted.`)) return;
    await api.deleteConnection(conn.id);
    toast.success("Connection removed");
    load();
  };

  const onResubscribe = async (conn) => {
    setBusy(conn.id);
    try {
      const res = await api.resubscribeConnection(conn.id);
      const s = res.subscription_result || {};
      if (s.ok) {
        toast.success("Webhook re-subscribed ✓ — Instagram events will now flow.");
      } else {
        toast.error(`Re-subscribe failed: HTTP ${s.status} — ${(s.body || "").slice(0, 200)}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Re-subscribe failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Layout
      title="Connections"
      subtitle="Pick which Instagram Business accounts Megatron should monitor. You can enable automation on any account linked to a Facebook Page you manage."
      action={
        <button onClick={load} data-testid="refresh-connections"
          className="inline-flex items-center gap-2 rounded-sm bg-zinc-950 px-6 py-2.5 text-sm font-bold text-white hover:bg-zinc-800">
          <ArrowsClockwiseIcon size={16} weight="bold" /> Refresh
        </button>
      }
    >
      {loading ? (
        <div className="rounded-sm border border-zinc-200 bg-white p-12 text-center text-zinc-400">Loading your pages…</div>
      ) : pages.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-sm border border-dashed border-zinc-300 bg-white py-24">
          <PlugIcon size={64} weight="duotone" className="text-zinc-300" />
          <h3 className="mt-4 font-display text-2xl font-bold text-zinc-900">No Facebook Pages found</h3>
          <p className="mt-2 max-w-md text-center text-sm text-zinc-500">
            Megatron needs a Facebook Page with a linked Instagram Business account. Make sure your IG account is set to
            Business (not Personal) and connected to a Facebook Page.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {pages.map((p) => {
            const ig = p.instagram_business_account;
            const conn = ig && enabledFor(ig.id);
            return (
              <div key={p.id} data-testid={`page-card-${p.id}`}
                className="flex flex-col rounded-sm border border-zinc-200 bg-white p-5 shadow-sm">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Facebook Page</p>
                <h3 className="mt-1 font-display text-lg font-bold tracking-tight text-zinc-950">{p.name}</h3>
                {ig ? (
                  <div className="mt-4 flex items-center gap-3 rounded-sm border border-zinc-100 bg-zinc-50 p-3">
                    <InstagramLogoIcon size={24} weight="duotone" className="text-pink-500" />
                    <div className="flex-1 min-w-0">
                      <p className="truncate font-mono text-sm text-zinc-900">@{ig.username}</p>
                      <p className="text-[10px] text-zinc-500">IG Business Account</p>
                    </div>
                  </div>
                ) : (
                  <p className="mt-4 text-xs text-zinc-500">No Instagram Business account linked to this page.</p>
                )}
                <div className="mt-5 flex items-center justify-between">
                  {conn ? (
                    <>
                      <div className="flex items-center gap-2">
                        <Switch checked={conn.enabled} onCheckedChange={(v) => onToggle(conn, v)}
                          data-testid={`toggle-conn-${conn.id}`} />
                        <span className={`text-xs font-bold uppercase tracking-wider ${conn.enabled ? "text-emerald-700" : "text-zinc-400"}`}>
                          {conn.enabled ? "Active" : "Paused"}
                        </span>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => onResubscribe(conn)}
                          disabled={busy === conn.id}
                          data-testid={`resubscribe-${conn.id}`}
                          title="Re-register Instagram webhook subscription (use if comments aren't triggering automation)"
                          className="rounded-sm p-2 text-zinc-500 hover:bg-amber-50 hover:text-amber-600 disabled:opacity-50"
                        >
                          <BroadcastIcon size={16} />
                        </button>
                        <button onClick={() => onDelete(conn)} data-testid={`delete-conn-${conn.id}`}
                          className="rounded-sm p-2 text-zinc-500 hover:bg-red-50 hover:text-red-600">
                          <TrashIcon size={16} />
                        </button>
                      </div>
                    </>
                  ) : ig ? (
                    <button onClick={() => onEnable(p)} disabled={busy === p.id}
                      data-testid={`enable-${p.id}`}
                      className="w-full rounded-sm bg-amber-500 px-4 py-2 text-sm font-bold text-zinc-950 hover:bg-amber-600 disabled:opacity-50">
                      {busy === p.id ? "Connecting…" : "Enable automation"}
                    </button>
                  ) : (
                    <span className="text-xs text-zinc-400">Cannot connect</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Layout>
  );
}
